// ── Right-side node drawer shell ─────────────────────────────────────────
//
// All config for the selected canvas node happens here. Reads the selection
// straight from the store (`selectedWorkflowId` + `selectedNodeId`) so
// WorkflowEditor only has to mount `<NodeDrawer />` unconditionally in its
// right rail — this component renders nothing when there's no selection.
import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Box, Building2, Flag, GitBranch, ListChecks, Play, Radar, Scale, ScanFace, ShieldAlert, Smartphone, UserCheck, Webhook as WebhookIcon, X } from 'lucide-react'
import { END_ID, START_ID, WEBHOOK_ID } from '../../graph'
import { useOpsCenterStore } from '../../store'
import type { BranchTask, StepName, StepTask, Task } from '../../types'
import { isBranchTask, isStepTask } from '../../types'
import { findTaskById, updateTaskById } from '../taskOps'
import { Field, ReadonlyValue, SectionHeader } from './fields'
import { AmlDrawer } from './AmlDrawer'
import { KybDrawer } from './KybDrawer'
import { FraudDrawer } from './FraudDrawer'
import { BranchDrawer } from './BranchDrawer'
import { ConfigFilesSection } from './ConfigFilesSection'
import { IdvDrawer } from './IdvDrawer'
import { KycDrawer } from './KycDrawer'
import { MatchlistDrawer } from './MatchlistDrawer'
import { WebhookDrawer } from './WebhookDrawer'

const STEP_STYLE: Record<StepName, { icon: LucideIcon; color: string; label: string }> = {
  MATCHLIST: { icon: ListChecks, color: '#a78bfa', label: 'Matchlist' },
  IDV: { icon: ScanFace, color: '#818cf8', label: 'IDV' },
  KYC: { icon: UserCheck, color: '#38bdf8', label: 'KYC' },
  AML: { icon: ShieldAlert, color: '#fb7185', label: 'AML' },
  KYB: { icon: Building2, color: '#2dd4bf', label: 'KYB' },
  FRAUD: { icon: Smartphone, color: '#fb923c', label: 'Fraud & Device Risk' },
  DECISION: { icon: Scale, color: '#fbbf24', label: 'Decision' },
  TOGGLEMONITORING: { icon: Radar, color: '#34d399', label: 'Monitoring' },
}

const FALLBACK_STEP_STYLE = { icon: Box, color: '#94a3b8', label: 'Step' }

function InfoPanel({ text }: { text: string }) {
  return <p className="text-xs text-[#5b6b85]">{text}</p>
}

// Generic read-only view of a step's workflow parameters, for step types that
// don't have a dedicated editor yet (e.g. DECISION, TOGGLEMONITORING).
function GenericParamsView({ params }: { params: Record<string, unknown> }) {
  const entries = Object.entries(params)
  return (
    <div className="flex flex-col gap-4">
      <SectionHeader title="Existing ops center workflow config" />
      {entries.length === 0 ? (
        <InfoPanel text="No parameters in the workflow." />
      ) : (
        entries.map(([k, v]) => (
          <Field key={k} label={k}>
            <ReadonlyValue>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</ReadonlyValue>
          </Field>
        ))
      )}
      <SectionHeader title="Proposed additional config" hint="Mockup · not saved" />
      <InfoPanel text="No additional mockup options for this step type yet." />
    </div>
  )
}

export function NodeDrawer() {
  const wf = useOpsCenterStore((s) => s.workflows.find((w) => w.id === s.selectedWorkflowId))
  const selectedNodeId = useOpsCenterStore((s) => s.selectedNodeId)
  const selectNode = useOpsCenterStore((s) => s.selectNode)
  const updateTasks = useOpsCenterStore((s) => s.updateTasks)

  if (!wf || !selectedNodeId) return null

  const workflow = wf
  const nodeId = selectedNodeId

  const persist = (updater: (t: Task) => Task) => {
    updateTasks(workflow.id, updateTaskById(workflow.tasks, nodeId, updater))
  }
  const handleStepChange = (updater: (t: StepTask) => StepTask) => {
    persist((t) => updater(t as StepTask))
  }
  const handleBranchChange = (updater: (t: BranchTask) => BranchTask) => {
    persist((t) => updater(t as BranchTask))
  }

  let icon: LucideIcon = Box
  let color = '#94a3b8'
  let typeLabel = ''
  let name = ''
  let body: ReactNode = null
  let nodeKey: StepName | 'WEBHOOK' | undefined
  // Real-task metadata surfaced under the header, mirroring the ops center panel.
  let metaId: string | undefined
  let metaDescription: string | undefined

  if (selectedNodeId === WEBHOOK_ID) {
    icon = WebhookIcon
    color = '#f59e0b'
    typeLabel = 'Webhook'
    name = 'Webhook'
    nodeKey = 'WEBHOOK'
    body = <WebhookDrawer wf={wf} />
  } else if (selectedNodeId === START_ID) {
    icon = Play
    color = '#4ade80'
    typeLabel = 'Start'
    name = 'Start'
    body = <InfoPanel text="Entry point of the workflow. No configuration." />
  } else if (selectedNodeId === END_ID) {
    icon = Flag
    color = '#94a3b8'
    typeLabel = 'End'
    name = 'End'
    body = <InfoPanel text="Terminal node, reached once all steps complete. No configuration." />
  } else {
    const task = findTaskById(wf.tasks, selectedNodeId)
    if (!task) return null

    metaId = task.id
    metaDescription = task.description || 'No description'

    if (isBranchTask(task)) {
      icon = GitBranch
      color = '#eab308'
      typeLabel = 'Branch'
      name = task.name
      body = <BranchDrawer task={task} onChange={handleBranchChange} />
    } else if (isStepTask(task)) {
      const style = STEP_STYLE[task.configuration.name] ?? FALLBACK_STEP_STYLE
      icon = style.icon
      color = style.color
      typeLabel = style.label
      name = task.name
      nodeKey = task.configuration.name

      switch (task.configuration.name) {
        case 'IDV':
          body = <IdvDrawer task={task} onChange={handleStepChange} />
          break
        case 'KYC':
          body = <KycDrawer task={task} onChange={handleStepChange} />
          break
        case 'AML':
          body = <AmlDrawer task={task} onChange={handleStepChange} />
          break
        case 'KYB':
          body = <KybDrawer task={task} onChange={handleStepChange} />
          break
        case 'FRAUD':
          body = <FraudDrawer task={task} onChange={handleStepChange} />
          break
        case 'MATCHLIST':
          body = <MatchlistDrawer task={task} onChange={handleStepChange} />
          break
        default:
          // DECISION / TOGGLEMONITORING and any other imported step type: no
          // dedicated editor yet, but still surface the real workflow params.
          body = <GenericParamsView params={task.configuration.parameters ?? {}} />
          break
      }
    }
  }

  const Icon = icon

  return (
    <div className="flex flex-col h-full rounded-lg border border-[#1e2c44] bg-[#0f1729] overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[#1e2c44] shrink-0">
        <div
          className="flex items-center justify-center rounded-md shrink-0"
          style={{ width: 26, height: 26, background: `${color}22`, color }}
        >
          <Icon size={14} />
        </div>
        <h3 className="text-xs font-semibold text-[var(--text-primary)] truncate flex-1 min-w-0">{name}</h3>
        <span
          className="text-[9px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded shrink-0"
          style={{ background: `${color}22`, color }}
        >
          {typeLabel}
        </span>
        <button
          onClick={() => selectNode(null)}
          className="p-1 rounded text-[#5b6b85] hover:text-[var(--text-primary)] hover:bg-[#1a2740] shrink-0"
          title="Close"
        >
          <X size={14} />
        </button>
      </div>

      {metaId && (
        <div className="shrink-0 px-3 py-2 border-b border-[#1e2c44] flex flex-col gap-1">
          <p className="font-mono text-[10px] text-[#5b6b85] break-all">{metaId}</p>
          <p className="text-[10px] text-[#5b6b85]">
            <span className="uppercase tracking-wide">Description:</span> {metaDescription}
          </p>
        </div>
      )}

      {/* Key by node id so per-node mockup (local) state resets when switching nodes. */}
      <div key={nodeId} className="flex-1 min-h-0 overflow-y-auto p-3">{body}</div>

      {nodeKey && (
        <div className="shrink-0 border-t border-[#1e2c44] max-h-64 overflow-y-auto">
          <ConfigFilesSection nodeKey={nodeKey} wf={wf} taskId={selectedNodeId} />
        </div>
      )}
    </div>
  )
}
